import React, { useRef, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { entrepriseService } from '../services/entreprise.service';
import * as XLSX from 'xlsx';
import './TicketVente.css';

interface LigneVente {
  produitNom: string;
  quantite: number;
  prixUnitaire: number;
  remise: number;
  sousTotal: number;
}

interface Vente {
  id: number;
  numero: string;
  clientNom: string;
  typeClient: string;
  modePaiement: string;
  totalHT: number;
  totalTVA: number;
  totalTTC: number;
  remiseGlobale: number;
  tvaActive: boolean;
  montantRecu: number;
  monnaie: number;
  statut: string;
  notes: string;
  createdAt: string;
  lignes: LigneVente[];
}

interface Config {
  nomEntreprise: string;
  adresse: string;
  telephone: string;
  email: string;
  ifu: string;
  rccm: string;
  siteWeb: string;
  piedPage: string;
}

interface Props {
  vente: Vente;
  config?: Partial<Config>;
  onFermer?: () => void;
  modeThermique?: boolean;
  autoImprimer?: boolean;
}

const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

const fmtPdf = (n: number): string => {
  const num = Math.round(Number(n));
  const str = num.toString();
  const parts: string[] = [];
  for (let i = str.length; i > 0; i -= 3) {
    parts.unshift(str.slice(Math.max(0, i - 3), i));
  }
  return parts.join(' ');
};

const net = (texte: string): string => {
  if (!texte) return '';
  return texte
    .replace(/[àâä]/g, 'a')
    .replace(/[éèêë]/g, 'e')
    .replace(/[îï]/g, 'i')
    .replace(/[ôö]/g, 'o')
    .replace(/[ùûü]/g, 'u')
    .replace(/[ç]/g, 'c')
    .replace(/[À]/g, 'A')
    .replace(/[ÂÄ]/g, 'A')
    .replace(/[ÉÈÊË]/g, 'E')
    .replace(/[ÎÏ]/g, 'I')
    .replace(/[ÔÖ]/g, 'O')
    .replace(/[ÙÛÜ]/g, 'U')
    .replace(/[Ç]/g, 'C')
    .replace(/['\u2019]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/[«»]/g, '"')
    .replace(/FCFA/g, 'F CFA');
};

const modeLabelFr = (mode: string) => {
  const labels: any = {
    especes: 'Espèces',
    orange_money: 'Orange Money',
    moov_money: 'Moov Money',
    wave: 'Wave',
    credit: 'Crédit client',
    virement: 'Virement',
  };
  return labels[mode] || mode;
};

const modeLabelPdf = (mode: string) => {
  const labels: any = {
    especes: 'Especes',
    orange_money: 'Orange Money',
    moov_money: 'Moov Money',
    wave: 'Wave',
    credit: 'Credit client',
    virement: 'Virement',
  };
  return labels[mode] || mode;
};

// Valeurs neutres utilisées le temps du chargement (ou si l'API est injoignable
// sans cache local) — plus AUCUNE donnée de démo en dur.
const configDefaut: Config = {
  nomEntreprise: 'NexaCom',
  adresse: '',
  telephone: '',
  email: '',
  ifu: '',
  rccm: '',
  siteWeb: '',
  piedPage: 'Merci pour votre confiance !',
};

export default function TicketVente({
  vente,
  config,
  onFermer,
  modeThermique = false,
  autoImprimer = false,
}: Props) {
  // En-tête réel de l'entreprise, chargé du serveur (cache service).
  const [cfgEntreprise, setCfgEntreprise] = useState<Partial<Config>>({});
  // Priorité : valeurs serveur, puis surcharge éventuelle par le prop config.
  const cfg = { ...configDefaut, ...cfgEntreprise, ...config };
  const ticketRef = useRef<HTMLDivElement>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoCharge, setLogoCharge] = useState(false);

  useEffect(() => {
    entrepriseService.getInfos().then((infos) => {
      setCfgEntreprise({
        nomEntreprise: infos.nom || configDefaut.nomEntreprise,
        adresse: entrepriseService.adresseComplete(infos),
        telephone: infos.telephone,
        email: infos.email,
        ifu: infos.ifu,
        rccm: infos.rccm,
        siteWeb: infos.siteWeb,
        piedPage: infos.piedPage || configDefaut.piedPage,
      });
    });
  }, []);

  useEffect(() => {
    genererQRCode();
    verifierLogo();
  }, [vente]);

  const genererQRCode = async () => {
    try {
      const data = JSON.stringify({
        num: vente.numero,
        date: new Date(vente.createdAt).toLocaleDateString('fr-FR'),
        client: vente.clientNom,
        total: vente.totalTTC,
        statut: vente.statut,
      });
      const url = await QRCode.toDataURL(data, {
        width: 80,
        margin: 1,
        color: { dark: '#000000', light: '#FFFFFF' },
      });
      setQrCodeUrl(url);
    } catch (e) {}
  };

  const verifierLogo = () => {
    const logo = localStorage.getItem('nexacom_logo');
    if (logo) setLogoUrl(logo);
    setLogoCharge(true);
  };

  const lancerImpression = (thermique: boolean) => {
    const titreInitial = document.title;
    document.title = `Ticket-${vente.numero}`;

    // Définit le format de page (@page) selon le mode
    const styleEl = document.createElement('style');
    styleEl.id = 'style-impression-ticket';
    styleEl.textContent = thermique
      ? '@page { size: 80mm auto; margin: 2mm; }'
      : '@page { size: A4; margin: 10mm; }';
    document.head.appendChild(styleEl);

    if (thermique) {
      document.body.classList.add('impression-thermique');
    }

    const nettoyer = () => {
      document.body.classList.remove('impression-thermique');
      document.title = titreInitial;
      document.getElementById('style-impression-ticket')?.remove();
      window.removeEventListener('afterprint', nettoyer);
    };
    window.addEventListener('afterprint', nettoyer);
    window.print();
    // Filet de sécurité si afterprint ne se déclenche pas
    setTimeout(nettoyer, 1500);
  };

  const imprimerStandard = () => lancerImpression(false);
  const imprimerThermique = () => lancerImpression(true);

  // Référence stable pour l'impression automatique
  const imprimerRef = useRef(imprimerStandard);
  imprimerRef.current = imprimerStandard;

  // Impression automatique quand tout est chargé
  useEffect(() => {
    if (autoImprimer && qrCodeUrl && logoCharge) {
      const timer = setTimeout(() => {
        imprimerRef.current();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [autoImprimer, qrCodeUrl, logoCharge]);

  const exporterPDF = async () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a5' });
    const pageWidth = doc.internal.pageSize.getWidth();
    let startY = 15;

    if (logoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = logoUrl;
        await new Promise(resolve => { img.onload = resolve; });
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        const imgData = canvas.toDataURL('image/png');
        const logoH = 15;
        const logoW = (img.width / img.height) * logoH;
        doc.addImage(imgData, 'PNG', 10, 8, logoW, logoH);
        startY = 28;
      } catch {}
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(194, 65, 12);
    doc.text(net(cfg.nomEntreprise), pageWidth / 2, startY, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(net(cfg.adresse), pageWidth / 2, startY + 6, { align: 'center' });
    doc.text(`Tel: ${cfg.telephone} | ${cfg.email}`, pageWidth / 2, startY + 11, { align: 'center' });
    doc.text(`IFU: ${cfg.ifu} | RCCM: ${cfg.rccm}`, pageWidth / 2, startY + 16, { align: 'center' });

    doc.setDrawColor(194, 65, 12);
    doc.setLineWidth(0.5);
    doc.line(10, startY + 19, pageWidth - 10, startY + 19);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('FACTURE / RECU DE VENTE', pageWidth / 2, startY + 26, { align: 'center' });

    const infoY = startY + 33;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`N : ${vente.numero}`, 10, infoY);
    doc.text(`Date : ${new Date(vente.createdAt).toLocaleString('fr-FR')}`, 10, infoY + 5);
    doc.text(net(`Client : ${vente.clientNom}`), 10, infoY + 10);
    doc.text(`Mode paiement : ${modeLabelPdf(vente.modePaiement)}`, 10, infoY + 15);

    autoTable(doc, {
      startY: infoY + 20,
      head: [['Designation', 'Qte', 'Prix unit.', 'Remise', 'Total']],
      body: vente.lignes?.map(l => [
        net(l.produitNom),
        l.quantite,
        `${fmtPdf(l.prixUnitaire)} F`,
        `${l.remise}%`,
        `${fmtPdf(l.sousTotal)} F`,
      ]) || [],
      headStyles: {
        fillColor: [194, 65, 12],
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 247, 242] },
      styles: { font: 'helvetica' },
    });

    const finalY = (doc as any).lastAutoTable.finalY + 5;
    let totalY = finalY;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    if (vente.remiseGlobale > 0) {
      doc.text(`Remise globale: ${vente.remiseGlobale}%`, pageWidth - 10, totalY, { align: 'right' });
      totalY += 5;
    }
    if (vente.tvaActive && vente.totalTVA > 0) {
      doc.text(`TVA 18%: ${fmtPdf(vente.totalTVA)} F CFA`, pageWidth - 10, totalY, { align: 'right' });
      totalY += 5;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(194, 65, 12);
    doc.text(`TOTAL TTC: ${fmtPdf(vente.totalTTC)} F CFA`, pageWidth - 10, totalY + 2, { align: 'right' });
    totalY += 8;

    if (vente.modePaiement === 'especes' && vente.montantRecu > 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(`Recu: ${fmtPdf(vente.montantRecu)} F CFA`, pageWidth - 10, totalY + 2, { align: 'right' });
      doc.text(`Monnaie: ${fmtPdf(vente.monnaie)} F CFA`, pageWidth - 10, totalY + 7, { align: 'right' });
      totalY += 10;
    }

    if (qrCodeUrl) {
      doc.addImage(qrCodeUrl, 'PNG', 10, finalY, 25, 25);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Scanner pour verifier', 10, finalY + 27);
    }

    const piedY = Math.max(totalY + 10, finalY + 30);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(net(cfg.piedPage), pageWidth / 2, piedY, { align: 'center' });
    doc.setDrawColor(194, 65, 12);
    doc.line(10, piedY + 3, pageWidth - 10, piedY + 3);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(cfg.siteWeb, pageWidth / 2, piedY + 7, { align: 'center' });

    doc.save(`Facture-${vente.numero}.pdf`);
  };

  const exporterExcel = () => {
    const wb = XLSX.utils.book_new();
    const donnees = [
      ['NEXACOM - DETAIL DE VENTE'],
      [],
      ['Numero', vente.numero],
      ['Date', new Date(vente.createdAt).toLocaleString('fr-FR')],
      ['Client', vente.clientNom],
      ['Type client', vente.typeClient],
      ['Mode paiement', modeLabelFr(vente.modePaiement)],
      ['Statut', vente.statut],
      [],
      ['PRODUITS'],
      ['Designation', 'Quantite', 'Prix unitaire', 'Remise %', 'Sous-total'],
      ...(vente.lignes?.map(l => [
        l.produitNom,
        l.quantite,
        l.prixUnitaire,
        `${l.remise}%`,
        l.sousTotal,
      ]) || []),
      [],
      ['TOTAUX'],
      ['Total HT', vente.totalHT],
      ['TVA 18%', vente.totalTVA],
      ['Remise globale', `${vente.remiseGlobale}%`],
      ['TOTAL TTC', vente.totalTTC],
      ['Montant recu', vente.montantRecu],
      ['Monnaie rendue', vente.monnaie],
    ];

    const ws = XLSX.utils.aoa_to_sheet(donnees);
    ws['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Vente');
    XLSX.writeFile(wb, `Vente-${vente.numero}.xlsx`);
  };

  return (
    <div className="ticket-overlay">
      <div className="ticket-container">

        {/* BARRE D'ACTIONS */}
        <div className="ticket-actions no-print">
          <div className="ticket-actions-titre">
            🧾 Ticket — {vente.numero}
            {autoImprimer && (
              <span className="auto-print-badge"> 🖨️ Impression auto...</span>
            )}
          </div>
          <div className="ticket-actions-btns">
            <button className="ta-btn standard" onClick={() => imprimerStandard()}>
              🖨️ Imprimer A4
            </button>
            <button className="ta-btn thermique" onClick={() => imprimerThermique()}>
              🧾 Thermique 80mm
            </button>
            <button className="ta-btn pdf" onClick={() => exporterPDF()}>
              📄 Export PDF
            </button>
            <button className="ta-btn excel" onClick={exporterExcel}>
              📊 Export Excel
            </button>
            {onFermer && (
              <button className="ta-btn fermer" onClick={onFermer}>✕ Fermer</button>
            )}
          </div>
        </div>

        {/* TICKET IMPRIMABLE */}
        <div
          ref={ticketRef}
          className={`ticket-document zone-impression ${modeThermique ? 'thermique' : 'standard'}`}>

          <div className="ticket-entete">
            <div className="ticket-logo-zone">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="ticket-logo-img" />
              ) : (
                <div className="ticket-logo-texte">{cfg.nomEntreprise}</div>
              )}
            </div>
            <div className="ticket-entreprise-info">
              <div>{cfg.adresse}</div>
              <div>Tél : {cfg.telephone}</div>
              <div>{cfg.email}</div>
              <div>IFU : {cfg.ifu}</div>
              <div>RCCM : {cfg.rccm}</div>
            </div>
          </div>

          <div className="ticket-separateur">{'─'.repeat(40)}</div>
          <div className="ticket-titre">FACTURE / REÇU DE VENTE</div>

          <div className="ticket-infos">
            <div className="ti-row">
              <span>N° Facture</span>
              <span className="ti-val">{vente.numero}</span>
            </div>
            <div className="ti-row">
              <span>Date</span>
              <span className="ti-val">{new Date(vente.createdAt).toLocaleString('fr-FR')}</span>
            </div>
            <div className="ti-row">
              <span>Client</span>
              <span className="ti-val">{vente.clientNom}</span>
            </div>
            <div className="ti-row">
              <span>Paiement</span>
              <span className="ti-val">{modeLabelFr(vente.modePaiement)}</span>
            </div>
            {vente.statut === 'credit' && (
              <div className="ti-row credit">
                <span>⚠️ Statut</span>
                <span className="ti-val">Vente à crédit</span>
              </div>
            )}
          </div>

          <div className="ticket-separateur">{'─'.repeat(40)}</div>

          <div className="ticket-lignes">
            <div className="tl-header">
              <span className="tl-nom">Désignation</span>
              <span className="tl-qte">Qté</span>
              <span className="tl-prix">P.U.</span>
              <span className="tl-total">Total</span>
            </div>
            <div className="ticket-separateur-fin">{'·'.repeat(40)}</div>
            {vente.lignes?.map((l, i) => (
              <div key={i} className="tl-ligne">
                <div className="tl-produit-nom">{l.produitNom}</div>
                <div className="tl-produit-detail">
                  <span className="tl-qte">{l.quantite}</span>
                  <span className="tl-prix">{fmt(l.prixUnitaire)} F</span>
                  {l.remise > 0 && <span className="tl-remise">-{l.remise}%</span>}
                  <span className="tl-total">{fmt(l.sousTotal)} F</span>
                </div>
              </div>
            ))}
          </div>

          <div className="ticket-separateur">{'─'.repeat(40)}</div>

          <div className="ticket-totaux">
            <div className="tt-row">
              <span>Sous-total HT</span>
              <span>{fmt(vente.totalHT)} FCFA</span>
            </div>
            {vente.remiseGlobale > 0 && (
              <div className="tt-row">
                <span>Remise {vente.remiseGlobale}%</span>
                <span>-{fmt(vente.totalHT * vente.remiseGlobale / 100)} FCFA</span>
              </div>
            )}
            {vente.tvaActive && vente.totalTVA > 0 && (
              <div className="tt-row">
                <span>TVA 18%</span>
                <span>{fmt(vente.totalTVA)} FCFA</span>
              </div>
            )}
            <div className="tt-row total">
              <span>TOTAL TTC</span>
              <span>{fmt(vente.totalTTC)} FCFA</span>
            </div>
            {vente.modePaiement === 'especes' && vente.montantRecu > 0 && (
              <>
                <div className="tt-row">
                  <span>Reçu</span>
                  <span>{fmt(vente.montantRecu)} FCFA</span>
                </div>
                <div className="tt-row monnaie">
                  <span>Monnaie</span>
                  <span>{fmt(vente.monnaie)} FCFA</span>
                </div>
              </>
            )}
          </div>

          <div className="ticket-separateur">{'─'.repeat(40)}</div>

          <div className="ticket-pied">
            <div className="ticket-pied-gauche">
              {qrCodeUrl && (
                <div className="ticket-qr">
                  <img src={qrCodeUrl} alt="QR Code" width={70} height={70} />
                  <div className="qr-label">Scanner pour vérifier</div>
                </div>
              )}
            </div>
            <div className="ticket-pied-droit">
              <div className="ticket-merci">{cfg.piedPage}</div>
              <div className="ticket-site">{cfg.siteWeb}</div>
              {vente.notes && <div className="ticket-notes">📝 {vente.notes}</div>}
            </div>
          </div>

          <div className="ticket-separateur">{'─'.repeat(40)}</div>
          <div className="ticket-mention">
            Document genere par NexaCom — {new Date().toLocaleDateString('fr-FR')}
          </div>
        </div>
      </div>
    </div>
  );
}